import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import { format, parse, startOfWeek, getDay, addMonths, subMonths } from "date-fns";
import { enUS } from "date-fns/locale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import DashboardHeader from "@/components/DashboardHeader";
import BackButton from "@/components/BackButton";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  Video,
  X,
  ExternalLink,
  AlertCircle,
} from "lucide-react";
import {
  fmtDateTime,
  fmtTime,
  getMyCalendar,
  isJoinable,
  statusBadge,
  typeBadge,
  typeEventBg,
} from "@/lib/schedule";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales,
});

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const dashboardHome =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "teacher"
        ? "/teacher"
        : user?.role === "student"
          ? "/student"
          : "/";
  const [view, setView] = useState(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const range = useMemo(() => {
    const from = subMonths(date, 1);
    const to = addMonths(date, 2);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [date]);

  useEffect(() => {
    setLoading(true);
    getMyCalendar({ from: range.from, to: range.to })
      .then(setSessions)
      .finally(() => setLoading(false));
  }, [range.from, range.to]);

  // Re-evaluate "joinable" every 30s so the button enables on time.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  const events = useMemo(
    () =>
      sessions.map((s) => ({
        id: s.id,
        title: s.title,
        start: new Date(s.starts_at),
        end: new Date(s.ends_at),
        resource: s,
      })),
    [sessions]
  );

  const eventStyleGetter = (event) => {
    const bg = typeEventBg(event.resource.session_type);
    return {
      style: {
        backgroundColor: bg,
        borderColor: bg,
        color: "#fff",
        borderRadius: 4,
        padding: "2px 4px",
        fontSize: 12,
      },
    };
  };

  const onSelect = (event) => setSelected(event.resource);

  const joinable = selected && isJoinable(selected);
  // tick is referenced to force memo refresh via the parent re-render
  void tick;

  return (
    <div
      className="min-h-screen bg-stone-50 text-stone-900"
      data-testid="calendar-page"
    >
      <DashboardHeader subtitle="My calendar · Live classes" />
      <main className="mx-auto max-w-7xl px-6 py-6">
        <BackButton to={dashboardHome} label="Dashboard" className="mb-3" />
        <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-amber-500 text-stone-900 flex items-center justify-center">
              <CalendarDays className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Calendar</h1>
              <p className="text-sm text-stone-500">
                Upcoming live classes you can join. Click an event for details.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-xs text-stone-500">
              <span className="inline-block h-3 w-3 rounded bg-amber-600" />
              Classwork
              <span className="inline-block h-3 w-3 rounded bg-red-600 ml-2" />
              Test
              <span className="inline-block h-3 w-3 rounded bg-stone-600 ml-2" />
              General
            </div>
            <div
              className="inline-flex rounded-md border border-stone-200 bg-white p-0.5"
              data-testid="view-toggle"
            >
              <Button
                size="sm"
                variant={view === Views.MONTH ? "default" : "ghost"}
                onClick={() => setView(Views.MONTH)}
                className={view === Views.MONTH ? "bg-stone-900 text-white" : ""}
                data-testid="view-month"
              >
                Month
              </Button>
              <Button
                size="sm"
                variant={view === Views.WEEK ? "default" : "ghost"}
                onClick={() => setView(Views.WEEK)}
                className={view === Views.WEEK ? "bg-stone-900 text-white" : ""}
                data-testid="view-week"
              >
                Week
              </Button>
              <Button
                size="sm"
                variant={view === Views.AGENDA ? "default" : "ghost"}
                onClick={() => setView(Views.AGENDA)}
                className={view === Views.AGENDA ? "bg-stone-900 text-white" : ""}
                data-testid="view-agenda"
              >
                Agenda
              </Button>
            </div>
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          <Card className="border-stone-200">
            <CardContent className="p-2 sm:p-4">
              <div data-testid="rbc-wrapper">
                <Calendar
                  localizer={localizer}
                  events={events}
                  view={view}
                  onView={setView}
                  date={date}
                  onNavigate={setDate}
                  views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
                  startAccessor="start"
                  endAccessor="end"
                  style={{ height: 640 }}
                  eventPropGetter={eventStyleGetter}
                  onSelectEvent={onSelect}
                  popup
                />
              </div>
              {loading && (
                <div className="text-xs text-stone-500 mt-2" data-testid="calendar-loading">
                  Loading sessions…
                </div>
              )}
            </CardContent>
          </Card>

          {/* Side panel */}
          <Card className="border-stone-200 h-fit" data-testid="session-side-panel">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">
                {selected ? "Session details" : `Sessions (${sessions.length})`}
              </CardTitle>
              {selected && (
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setSelected(null)}
                  data-testid="side-panel-close"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-3">
              {selected ? (
                <div className="space-y-3" data-testid="session-detail">
                  <div className="space-y-1">
                    <div className="text-sm font-semibold">{selected.title}</div>
                    <div className="text-xs text-stone-500">
                      {fmtDateTime(selected.starts_at)} – {fmtTime(selected.ends_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={typeBadge(selected.session_type)}>
                      {selected.session_type}
                    </Badge>
                    <Badge variant="outline" className={statusBadge(selected.status)}>
                      {selected.status}
                    </Badge>
                  </div>
                  {selected.notes && (
                    <p className="text-xs text-stone-600">{selected.notes}</p>
                  )}
                  <div className="pt-2 flex flex-col gap-2">
                    <Button
                      className={
                        joinable
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : "bg-stone-300 text-stone-600 cursor-not-allowed"
                      }
                      disabled={!joinable}
                      onClick={() => navigate(`/sessions/${selected.id}/live`)}
                      data-testid="side-join-button"
                    >
                      <Video className="mr-2 h-4 w-4" />
                      {joinable ? "Join live class" : "Join opens ±15 min of start"}
                    </Button>
                    {selected.external_link && (
                      <a
                        href={selected.external_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"
                        data-testid="side-external-link"
                      >
                        <ExternalLink className="h-3 w-3" /> External link
                      </a>
                    )}
                  </div>
                </div>
              ) : sessions.length === 0 && !loading ? (
                <div className="py-8 text-center text-sm text-stone-500" data-testid="no-sessions-side">
                  <AlertCircle className="mx-auto h-5 w-5 mb-1 text-stone-400" />
                  No sessions in this range yet.
                </div>
              ) : (
                <div className="space-y-2 max-h-[560px] overflow-auto">
                  {sessions
                    .filter((s) => new Date(s.ends_at) >= new Date())
                    .slice(0, 30)
                    .map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => setSelected(s)}
                        className="w-full text-left border border-stone-200 rounded-md px-3 py-2 hover:border-stone-400"
                        data-testid={`upcoming-${s.id}`}
                      >
                        <div className="text-xs font-medium truncate">{s.title}</div>
                        <div className="text-[11px] text-stone-500">
                          {fmtDateTime(s.starts_at)}
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
